
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContentText,
    DialogActions,
    DialogContent,
    Slide,
    Grid,
    TextField,
    Stepper,
    Step,
    StepLabel,
    LinearProgress
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {FileModel} from  '../../models/models';
import {BaseRecordModel,
        SelectRecordScreenElementModel,
        BaseSelectItemModel,
        SelectItemScreenElementModel,
        InputFieldScreenElementModel,
        InputFieldElementType,
        OutputFieldScreenElementModel,
        TitleScreenElementModel,
        Jinja2BodyScreenElementModel} from '../../models/modelsFlow';
import {ScreenElementTypeModel} from '../../models/modelsFlow';
import {FlowRunModel,
        FlowScreenModel,
        FlowSuccessModel}  from '../../models/modelsFlow';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import Config from "../../config";
import { useAuth } from "../../hooks/useAuth";
import {SelectRecordElementComponent} from "./SelectRecordElement";
import {SelectItemElementComponent} from "./SelectItemElement";
import {InputFieldElementComponent} from "./InputFieldElement";
import {OutputFieldElementComponent} from "./OutputFieldElement";
import CloseIcon from "@mui/icons-material/Close";
import { executeFlowStep } from "../../services/flowApi";

export const FlowScreenComponent: React.FC<{
  flowRun: FlowRunModel,
  flowScreen: FlowScreenModel
}> = ({
  flowRun,
  flowScreen
}) => {

  const styles = ContainerClasses();

  let {appName, objectName, recordUuid} = useParams();
  const {accessToken, onInvalidToken} = useAuth();

  const { enqueueSnackbar } = useSnackbar();


  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false)

  const [mutableScreen, setMutableScreen] = useState<FlowScreenModel>(flowScreen);
  const [mutableRun, setMutableRun] = useState<FlowRunModel>(flowRun);

  const [isExecutingFlowStep, setIsExecutingFlowStep] = useState<boolean>(false);

  useEffect(() => {
    if(flowRun){
      setMutableRun(flowRun);
    }
    if(flowScreen){
      setMutableScreen(flowScreen);
      setOpenDialog(true);
    } else {
      setOpenDialog(false);
    }
  }, [flowScreen, flowRun]);

  useEffect(() => {
    if(errorMessage){
     enqueueSnackbar(errorMessage, {
          variant: "error",
       });
    setErrorMessage(null)
    }

    if(successMessage){
     enqueueSnackbar(successMessage, {
          variant: "success",
       });
    setSuccessMessage(null)
    }
  }, [errorMessage, successMessage]);


  const onCloseDialog = (event: any) => {
    setOpenDialog(false)
  }

  const handleNext = () => {
    setIsExecutingFlowStep(true);
    executeFlowStep(accessToken, mutableRun, mutableScreen)
       .then((data) => {
         if(data.flowSuccess){
           setSuccessMessage(data.flowSuccess.message)
           setOpenDialog(false)
         }
         if(data.flowRun) {
           setMutableRun(data.flowRun)
         }
         if(data.flowScreen){
           setMutableScreen(data.flowScreen)
         }
         setIsExecutingFlowStep(false);
       })
       .catch((err) => {
         setIsExecutingFlowStep(false);
         setErrorMessage(err.message)
       })
  }

  const checkScreen = () => {
    if(isExecutingFlowStep) return true;

    const validations = mutableScreen.elements.map((thisElement: ScreenElementTypeModel) => {
      return thisElement.validate();
    })

    return validations.includes(false);

  }

  const titleElement = (element: TitleScreenElementModel) => {
    return (
      <Grid item xs={12} md={12} key={`${element.component}-${element.rank}`}>
         <Typography variant="h6">
            {element.value}
         </Typography>
      </Grid>
    )
  }

  const jinja2BodyElement = (element: Jinja2BodyScreenElementModel) => {
    return (
      <Grid item xs={12} md={12} key={`${element.component}-${element.rank}`}>
        <Box sx={{
               padding: 3,
               '& img': {maxWidth: '100%'},
               '& table': {maxWidth: '100%'}
             }}
          dangerouslySetInnerHTML={{__html: element.body}}
        />
      </Grid>
    )
  }

  const selectRecordElement = (element: SelectRecordScreenElementModel) => {
    return (
      <Grid item xs={6} md={6} key={`${element.component}-${element.rank}`}>
        <SelectRecordElementComponent
          element={element}
          onChange={editSelectRecordElement}
        />
      </Grid>
    )
  }
  const editSelectRecordElement = (value: BaseRecordModel, selectedElement: SelectRecordScreenElementModel) => {
    setMutableScreen(prevState => ({
      ...prevState,
      elements: prevState.elements.map((thisElement: ScreenElementTypeModel) => {
        if(thisElement.rank == selectedElement.rank &&
           thisElement.component == selectedElement.component){
             const thisSelectElement = thisElement as SelectRecordScreenElementModel;
             return new SelectRecordScreenElementModel(thisSelectElement.apiName, thisSelectElement.rank, thisSelectElement.component, thisSelectElement.records, value)
           }
        return thisElement
      })
    }));
  }

  const selectItemElement = (element: SelectItemScreenElementModel) => {
    return (
      <Grid item xs={6} md={6} key={`${element.component}-${element.rank}`}>
        <SelectItemElementComponent
          element={element}
          onChange={editSelectItemElement}
        />
      </Grid>
    )
  }
  const editSelectItemElement = (value: BaseSelectItemModel, selectedElement: SelectItemScreenElementModel) => {
    setMutableScreen(prevState => ({
      ...prevState,
      elements: prevState.elements.map((thisElement: ScreenElementTypeModel) => {
        if(thisElement.rank == selectedElement.rank &&
           thisElement.component == selectedElement.component){
             const thisSelectElement = thisElement as SelectItemScreenElementModel;
             return new SelectItemScreenElementModel(thisSelectElement.apiName, thisSelectElement.rank, thisSelectElement.component, thisSelectElement.items, value)
           }
        return thisElement
      })
    }));
  }

  const inputFieldElement = (element: InputFieldScreenElementModel) => {
    return (
      <Grid item xs={6} md={6} key={`${element.component}-${element.rank}`}>
        <InputFieldElementComponent
          element={element}
          onChange={editInputFieldElement}
        />
      </Grid>
    )
  }
  const editInputFieldElement = (value: InputFieldElementType, element: InputFieldScreenElementModel, file: File | null ) => {

    setMutableScreen(prevState => {

      const files = {...prevState.files}
      if(file === null) {
        delete files[element.apiName]
      } else {
        const thisFile = new FileModel(element.apiName, file.name, file)
        files[element.apiName] = thisFile
      }

      const elements = prevState.elements.map((thisElement: ScreenElementTypeModel) => {
        if(thisElement.rank == element.rank &&
           thisElement.component == element.component){
             const updatedElement = thisElement as InputFieldScreenElementModel;
             return new InputFieldScreenElementModel(updatedElement.apiName,
                                                     updatedElement.rank,
                                                     updatedElement.component,
                                                     updatedElement.label,
                                                     updatedElement.type,
                                                     updatedElement.maxLength,
                                                     updatedElement.visibleLines,
                                                     updatedElement.allowedExtensions,
                                                     value)
           }
        return thisElement
      })

      return {
        ...prevState,
        elements: elements,
        files: files
    }
  })
}

  const outputFieldElement = (element: OutputFieldScreenElementModel) => {
    return (
      <Grid item xs={12} md={12} key={`${element.component}-${element.rank}`}>
         <OutputFieldElementComponent
           element={element}
          />
      </Grid>
    )
  }



return (
    <Dialog
          open={openDialog}
          onClose={onCloseDialog}
          fullWidth
          maxWidth={'lg'}
//          fullScreen
          TransitionComponent={Slide}
          TransitionProps={{direction: "up"} as any}
          PaperProps={{
            sx: {
              display: 'flex',
              flexDirection: 'column',
              height: '70vh'
            }
          }}
    >

    <DialogTitle>
      <Toolbar sx={{justifyContent: 'space-between'}}>
         <Typography variant='h6'>
           {mutableScreen.title}
         </Typography>
         <IconButton
            edge="start"
            color="inherit"
            onClick={onCloseDialog}
            aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
    </DialogTitle>

    <DialogContent>
      {isExecutingFlowStep && <LinearProgress color="primary" />}
      <Grid
        container
        spacing={2}
      >
        {mutableScreen.elements.map((thisElement: any) => {
          if(thisElement.component == '<Title>') return titleElement(thisElement)
          if(thisElement.component == '<Jinja2Body>') return jinja2BodyElement(thisElement)
          if(thisElement.component == '<SelectRecord>') return selectRecordElement(thisElement)
          if(thisElement.component == '<SelectItem>') return selectItemElement(thisElement)
          if(thisElement.component == '<InputField>') return inputFieldElement(thisElement)
          if(thisElement.component == '<OutputField>') return outputFieldElement(thisElement)
          return (
            <Box key={'undefined'}>
               Flow-Screen-Component not implemented yet
            </Box>
          )
        })
       }
       </Grid>
     </DialogContent>

     <DialogContent>
       <Box sx={styles.containerRowD}>
         <Button
           variant="contained"
           color="primary"
           disabled = {checkScreen()}
           onClick={() => handleNext()}
          >
            Next
          </Button>
       </Box>
      </DialogContent>


    </Dialog>

  );

};
